import { useState } from 'react';
import { useSelector } from 'react-redux';

import ResourceModal from 'app/components/ResourceModal';
import ResourceModalPanel from 'app/components/ResourceModalPanel';
import CustomerUploadModal from 'app/components/Uploads/CustomerUploadModal';
import useMomentsUploadForm from 'app/hooks/useMomentsUploadForm';
import useDeeplink from 'app/hooks/useDeeplink';
import CustomerForm from './CustomerForm';
import CustomerUploadProgressBar from './CustomerUploadProgressBar';

import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LinkIcon from '@mui/icons-material/Link';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  notifyCustomer,
  getCustomerMoments,
  deleteCustomerMoments
} from 'app/services/customers';

import {
  togglePremiumMoment,
  getMoments
} from 'app/services/moments'

import {
  moments
} from 'app/resources';

import { STORY_MOMENTS_DOWNLOAD } from 'app/utils/Constants';

import {
  getLocalizedDate,
  getLocalizedTime,
  getDay
} from 'shared/utils/Utils';

const CustomerPanel = props => {
  const {
    customer,
    action,
    onClose,
    onDelete,
    onUploadStart,
    onUploadComplete
  } = props;

  const {
    formIsOpen,
    onAdd,
    onFormClose,
    onFormSubmit
  } = useMomentsUploadForm({
    onUploadStart,
    onUploadComplete,
    customer
  });

  const deeplink = useDeeplink();

  const account = useSelector(state => state.portalReducer.account);
  const showMomentUpload = !account.feature_flags.disable_upload;

  // if we're creating a customer then no actions panel will be shown
  if (action === 'create') {
    return null;
  }

  let download = () => {
    let link = document.createElement('a');

    link.href = STORY_MOMENTS_DOWNLOAD(customer.story_id);
    link.download = `${customer.id}_customer_moments`;
    link.click();
  }

  // get images for Moments card
  const getImages = async (page = 1, perPage = 100) => {
    let filterModel = {
      story_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: customer.story_id.toString()
      }]
    }

    let params = {
      page,
      per_page: perPage,
      filter_model: filterModel,
      sort_model: []
    }

    let moments = await getCustomerMoments(customer.id, page, perPage);

    let images = moments.map(moment => ({
      id: moment.id,
      src: moment.preview_url,
      w: moment.width,
      h: moment.height,
      filename: moment.filename,
      premium: moment.premium,
      original: moment.print_url,
      is_video: moment.type && moment.type.includes("video")
    }));

    return images;
  }

  const renderNotificationStatus = (notification) =>{
    switch (notification.status) {
      case 0:
        return 'Pending';
      case 1:
        return 'Sending';
      case 2:
        return 'Delivered';
      case 3:
        return 'Failed';
    }
  }

  const sendNotification = async () => {
    let params = {method: 0, customer_ids:[customer.id], force: true};
    await notifyCustomer(params);
  }

  const deleteMoment = async (moment) => {
    let params = {moment_ids: [moment.id]}
    return await deleteCustomerMoments(customer.id, params);
  }

  const toggleMoment = async (moment) => {
    let params = {moment_id: moment.id, premium: !moment.premium, customer_id: customer.id}
    return await togglePremiumMoment(moment.id, params);
  }

  const viewMoment = (moment, newTab = false) => {
    let filterModel = {
      id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: moment.id.toString(),
        operator: AND_OPERATOR
      }]
    }

    if (newTab) {
      // return link to set as anchor href with target="_blank"
      return deeplink.getLink(moments, filterModel);
    } else {
      deeplink.load(moments, filterModel);
    }
  }

  const getMomentsLink = () => {
    let filterModel = {
      story_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: customer.story_id.toString(),
        operator: AND_OPERATOR
      }]
    }

    // return link to set as anchor href with target="_blank"
    return deeplink.getLink(moments, filterModel);
  }

  let notificationsMessage = null;
  if (!customer?.notifications || !customer?.notifications.length) {
    notificationsMessage = 'No notifications have been sent.';
  }

  let notificationsDetails = customer.notifications?.map(n => {
    let day = getDay(n.created_at, 'short');
    let date = getLocalizedDate('en-us', n.created_at, 'short');
    let time = getLocalizedTime('en-us', n.created_at, 'short');
    let status = renderNotificationStatus(n);
    let divStyle = { margin: '0.5rem 0' };
    let pStyle = { margin: '0' };

    return {
      label: (
        <div style={divStyle}>
          <p style={pStyle}>{day} {date}</p>
          <p style={pStyle}>{time}</p>
        </div>
      ),
      value: status
    }
  });

  let momentActions = [];
  if (showMomentUpload) {
    momentActions.push(
      { label: 'Upload Moments', callback: onAdd }
    );
  }
  momentActions.push(
    { label: 'View Moments', newTabLink: getMomentsLink() }
  );

  let cards = [
    {
      label: 'Moments',
      getImages,
      imageCount: customer.photo_count,
      imagesPerPage: 100,
      progressBar: <CustomerUploadProgressBar customer={customer} inModal={true} />,
      details: [
        { label: 'Moments', value: customer.photo_count },
      ],
      actions: momentActions,
      psActions: [
        { key: "delete", icon: DeleteIcon, onClick: deleteMoment },
        { key: "togglePremium", icon: AttachMoneyIcon, onClick: toggleMoment },
        { key: "link", icon: LinkIcon, onClick: viewMoment },
      ]
    },
    {
      label: 'Download Moments',
      message: 'Download all customer moments.  Includes hidden moments',
      actions: [
        { label: 'Download Moments', callback: download }
      ]
    },
    {
      label: 'Notifications',
      message: notificationsMessage,
      details: notificationsDetails,
      actions: [
        { label: 'Send Notification', callback: sendNotification }
      ]
    },
    {
      label: 'Delete Customer',
      message: 'Deleting a customer will permanently remove their images and invalidate any links.',
      actions: [
        { label: 'Delete Customer', callback: onDelete }
      ]
    }
  ]

  return (
    <div>
      <ResourceModalPanel cards={cards} />
      <CustomerUploadModal
        isOpen={formIsOpen}
        onClose={onFormClose}
	onFormSubmit={onFormSubmit}
	customer={customer}
	center={true}
      />
    </div>
  );
}

const CustomerModal = props => {
  const {
    isOpen,
    onClose,
    onFormSubmit,
    onDestroy,
    customer,
    onUploadStart,
    onUploadComplete,
    onCreate,
    center,
    job,
    kioskOptions
  } = props;

  const action = customer ? 'update' : 'create';

  let title, subTitle = null;
  if (action === 'update') {
    title = customer.name || 'Unnamed Customer';
    subTitle = customer.email || customer.phone || customer.access_code || customer.id;
  } else {
    title = 'New Customer';
  }

  const onSubmit = params => onFormSubmit(action, params);
  const onDelete = () => onDestroy(customer.id);

  return (
    <ResourceModal {...{ isOpen, onClose, title, subTitle, center }}>
      <CustomerPanel {...{ customer, action, onClose, onDelete, onUploadStart, onUploadComplete }} />
      <CustomerForm {...{ customer, action, onClose, onSubmit, job, kioskOptions }} />
    </ResourceModal>
  );
}

export default CustomerModal;
